import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { CreditDebitExclusionFindInterface } from '@zaoblako/shared/modules/document/dtos/credit-debit/credit-debit-exclusion-find.dto';
import {
  CreditDebitExclusionFormDto,
  type CreditDebitExclusionFormInterface
} from '@zaoblako/shared/modules/document/dtos/credit-debit/credit-debit-exclusion-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DebtsExclusionService } from '@zaoblako/web/core/services/debts/debts-exclusion.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ExclusionHistoryComponent } from '@zaoblako/web/modules/debts/dumbs/exclusion-form/exclusion-history/exclusion-history.component';
import { DocumentLinkComponent } from '@zaoblako/web/shared/components/document-link/document-link.component';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-exclusion-form',
  templateUrl: './exclusion-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputNumberModule,
    FormsModule,
    ButtonComponent,
    ContragentAutoCompleteComponent,
    JoinPipe,
    EditorComponent,
    DatepickerComponent,
    FileUploaderComponent,
    DocumentLinkComponent,
    ExclusionHistoryComponent,
    SpinnerComponent,
    CompanySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExclusionFormComponent extends BaseFormComponent<CreditDebitExclusionFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public exclusion = <CreditDebitExclusionFindInterface>{};
  public dto = CreditDebitExclusionFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly debtsExclusionService: DebtsExclusionService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmDialogService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.debtsExclusionService
      .findById<CreditDebitExclusionFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (exclusion) => {
          this.exclusion = exclusion;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public delayDateMessage() {
    if (this.exclusion.defermentGrantedUntil) {
      const delayDate = moment(this.exclusion.defermentGrantedUntil).format('YYYY-MM-DD');
      const nowDate = moment().format('YYYY-MM-DD');
      if (delayDate < nowDate) {
        return this.errorService.addCustomError(
          'Дата отсрочки позже текущей даты, исключение будет автоматически удалено через сутки'
        );
      }
    }
    return;
  }

  public saveExclusion() {
    if (this.checkValidate(this.exclusion).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.debtsExclusionService
          .update<CreditDebitExclusionFormInterface, CreditDebitExclusionFindInterface>(this.exclusion)
          .subscribe({
            next: (exclusion) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Исключение №${exclusion?.id} обновлено`);
              this.formStoreService.saveNext(exclusion, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.debtsExclusionService.create<CreditDebitExclusionFormInterface>(this.exclusion).subscribe({
          next: (exclusion) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Исключение создано!');
            this.formStoreService.saveNext(exclusion, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteExclusion() {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить исключение №${this.exclusion?.id}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);

        this.debtsExclusionService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Исключение №${this.exclusion?.id} удалено!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
